/**
 * PanelBannerSlot - Persistent banner area displayed below the panel header,
 * above tab content, visible regardless of which tab is active.
 *
 * Handles persistent/contextual panel content:
 *   - Featured entity fallback (when no results)
 *   - "Don't see your vendor?" CTA
 *   - System / maintenance notices (future)
 *
 * Ephemeral notifications (onboarding, nearby entity, city proximity) live
 * in the floating ProximityNotificationStack in HomePageMap.
 *
 * @atomic - Single responsibility: named slot for persistent panel banners
 * @idempotent - Returns null when nothing to show
 */
import { memo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FeaturedEntityCard from './FeaturedEntityCard'

const PanelBannerSlot = memo(function PanelBannerSlot({
  onTabChange,
  onSpotClick,
  featuredEntity,
  showingFeaturedEntity,
  featuredEntityReason,
  onFeaturedViewDetails,
  onFeaturedShowOnMap,
  onFeaturedJoinChannel
}) {
  const { t } = useTranslation()
  // Dismiss state — resets each time no-results condition is detected (showingFeaturedEntity → true)
  const [featuredDismissed, setFeaturedDismissed] = useState(false)
  useEffect(() => {
    if (showingFeaturedEntity) setFeaturedDismissed(false)
  }, [showingFeaturedEntity])

  return (
    <div className="px-2 pt-1">
      {/* Featured entity — shown when no results, re-shown each time condition re-triggers */}
      {showingFeaturedEntity && featuredEntity && !featuredDismissed && (
        <FeaturedEntityCard
          className="mt-2"
          entity={featuredEntity}
          onViewDetails={onFeaturedViewDetails}
          onShowOnMap={onFeaturedShowOnMap}
          onJoinChannel={onFeaturedJoinChannel}
          reason={featuredEntityReason}
          onDismiss={() => setFeaturedDismissed(true)}
        />
      )}

      {/* Suggest / Spot CTA — shown when no results */}
      {showingFeaturedEntity && (
        <p className="text-center text-xs text-gray-400 mt-2 pb-1">
          {t('map.dontSeeVendor', "Don't see your vendor?")}{' '}
          <button
            type="button"
            className="text-primary hover:text-primary-600 hover:underline transition-colors"
            onClick={() => onSpotClick?.()}
            onTouchEnd={(e) => { e.preventDefault(); onSpotClick?.() }}
          >
            {t('map.spotItLink', 'Spot it')}
          </button>
          {' · '}
          <button
            type="button"
            className="text-primary hover:text-primary-600 hover:underline transition-colors"
            onClick={() => onTabChange?.('contact')}
            onTouchEnd={(e) => { e.preventDefault(); onTabChange?.('contact') }}
          >
            {t('map.suggestVendorLink', 'Suggest one')}
          </button>
        </p>
      )}

      {/* City proximity banners moved to floating overlay above the map (see HomePageMap CityProximityBannerWrapper) */}

      {/* ─── Future banners go here ───────────────────────────────────────────
       *
       *   Example — maintenance notice:
       *     <MaintenanceBanner />
       *
       *   Example — system error:
       *     <SystemAlertBanner />
       *
       * ─────────────────────────────────────────────────────────────────── */}
    </div>
  )
})

export default PanelBannerSlot
